import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { Spinner } from '../elements/Spinner';
import { GuardLayout } from '../layout/GuardLayout';
import { CampaignLayout } from '../layout/CampaignLayout';
import { AuthLayout } from '../layout/AuthLayout';
import { GeneralLayout } from '../layout/GeneralLayout';

// Lazy-loaded page components
const LandingPage = lazy(() =>
  import('../pages/LandingPage').then((m) => ({ default: m.LandingPage }))
);
const Dashboard = lazy(() =>
  import('../pages/dashboard').then((m) => ({ default: m.Dashboard }))
);
const AccountPage = lazy(() =>
  import('../pages/account').then((m) => ({ default: m.AccountPage }))
);
const ApiPage = lazy(() =>
  import('../pages/api').then((m) => ({ default: m.ApiPage }))
);
const CampaignsPage = lazy(() =>
  import('../pages/campaigns').then((m) => ({ default: m.CampaignsPage }))
);
const CampaignDetails = lazy(() => import('../pages/campaigns/markets'));
const EditMarketPage = lazy(() => import('../pages/campaigns/markets/edit'));
const EditCampaignPage = lazy(() => import('../pages/campaigns/edit'));
const SignInPage = lazy(() => import('../pages/auth/signin'));
const AdvertiserAccounts = lazy(() => import('../pages/admin/AdvertiserAccounts'));
const NotificationsDashboard = lazy(() => import('../pages/admin/NotificationsDashboard'));
const AdvertiserTerms = lazy(() => import('../pages/advertiser-terms'));
const MarketingComplianceAddendum = lazy(() => import('../pages/marketing-compliance-addendum'));
const AdvertiserFaq = lazy(() => import('../pages/faq'));
const ResultsPage = lazy(() => import('../pages/results'));
const DncAuditPage = lazy(() => import('../pages/dnc-audit'));
const FinanceOverviewPage = lazy(() => import('../pages/finance'));
const FinanceRefundsPage = lazy(() => import('../pages/finance/Refunds'));
const FinanceBillingPage = lazy(() => import('../pages/finance/Billing'));
const RevenueDashboard = lazy(() => import('../pages/admin/RevenueDashboard'));

const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Spinner />}>{children}</Suspense>
);

export const RoutesConfig: RouteObject[] = [
  // Landing page (standalone, handles its own layout)
  {
    path: '/',
    element: <S><LandingPage /></S>,
  },
  {
    element: <GuardLayout />,
    children: [
      {
        path: '/dashboard',
        element: <S><Dashboard /></S>,
      },
      {
        path: '/account',
        element: <S><AccountPage /></S>,
      },
      {
        path: '/api',
        element: <S><ApiPage /></S>,
      },
      {
        path: '/results',
        element: <S><ResultsPage /></S>,
      },
      {
        element: <CampaignLayout />,
        children: [
          {
            path: '/campaigns/:campaignId/markets/:marketId/edit',
            element: <S><EditMarketPage /></S>,
          },
          {
            path: '/campaigns/:id',
            element: <S><CampaignDetails /></S>,
          },
          {
            path: '/campaigns/:id/edit',
            element: <S><EditCampaignPage /></S>,
          },
          {
            path: '/campaigns',
            element: <S><CampaignsPage /></S>,
          },
        ],
      },
      {
        path: '/advertisers',
        element: <S><AdvertiserAccounts /></S>,
      },
      {
        path: '/notifications',
        element: <S><NotificationsDashboard /></S>,
      },
      {
        path: '/dnc-audit',
        element: <S><DncAuditPage /></S>,
      },
      {
        path: '/finance',
        element: <S><FinanceOverviewPage /></S>,
      },
      {
        path: '/finance/refunds',
        element: <S><FinanceRefundsPage /></S>,
      },
      {
        path: '/finance/billing',
        element: <S><FinanceBillingPage /></S>,
      },
      {
        path: '/revenue',
        element: <S><RevenueDashboard /></S>,
      },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      {
        path: '/signin',
        element: <S><SignInPage /></S>,
      },
    ],
  },
  {
    element: <GeneralLayout />,
    children: [
      {
        path: '/advertiser-terms',
        element: <S><AdvertiserTerms /></S>,
      },
      {
        path: '/marketing-compliance-addendum',
        element: <S><MarketingComplianceAddendum /></S>,
      },
      {
        path: '/faq',
        element: <S><AdvertiserFaq /></S>,
      },
    ],
  },
];
